/**
 * t224u: one checklist run as a compact phone row (My checklists and All checklists): the name, building · who · due
 * time, and progress as "3/10" over a slim bar. Overdue is red; completed rows are dimmed. Tapping opens the runner.
 */
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { checklistRunPeople, formatRegisterCounts, type ChecklistRun } from '@tradeoutlet/shared';
import { clock, dt, fmt, today } from '@/lib/format';

/** "Mon 19 Oct" for a run on another day (several days of the same checklist look alike otherwise). */
const dayTag = (local: string | undefined) => (local && local !== today() ? `${fmt.weekday(local)} ` : '');
export function checklistWhen(r: Pick<ChecklistRun, 'status' | 'due_by' | 'late' | 'completed_at'> & { local_date?: string }, doneBy?: string | null): string {
  const d = dayTag(r.local_date);
  // t703u: "Done by Sam 01:51" when the row names people.
  if (r.status === 'completed') return `Done ${doneBy ? `by ${doneBy} ` : ''}${d}${r.completed_at ? clock(dt(r.completed_at)) : ''}`.trim();
  if (r.status === 'missed' || r.late) return r.due_by ? `Overdue · ${d}${clock(dt(r.due_by))}` : `Overdue${d ? ` · ${d.trim()}` : ''}`;
  return r.due_by ? `Due ${d}${clock(dt(r.due_by))}` : d ? `${d.trim()}` : 'Any time';
}

export function ChecklistRow({ r, showWho = false }: { r: ChecklistRun; showWho?: boolean }) {
  const done = r.status === 'completed';
  const overdue = !done && (r.late || r.status === 'missed');
  const pct = r.item_count ? Math.round((r.answered_count / r.item_count) * 100) : 0;
  const people = showWho ? checklistRunPeople(r) : null;
  const who = people?.who ?? null;
  const when = checklistWhen(r, people?.doneBy);
  // t329u: a later day's checklist that hasn't been created yet: shown, not opened.
  if (r.status === 'scheduled') {
    return (
      <div data-testid="m-checklist-scheduled" className="flex min-h-[60px] items-center gap-3 border-b border-slate-100 px-4 py-2 dark:border-slate-800">
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[15px] font-medium">{r.template_name}</span>
          <span className="flex min-w-0 gap-1 text-xs text-slate-500">
            <span className="truncate">{[r.location_name, who].filter(Boolean).join(' · ')}</span><span className="shrink-0" data-testid="m-checklist-when">· {when}</span>
          </span>
        </span>
        <span className="shrink-0 text-xs text-slate-500">Scheduled</span>
      </div>
    );
  }
  return (
    <Link to={`/checklists/run/${r.id}`} data-testid="m-checklist-row" data-state={done ? 'done' : overdue ? 'overdue' : 'open'}
      className={clsx('flex min-h-[60px] items-center gap-3 border-b border-slate-100 px-4 py-2 active:bg-slate-50 dark:border-slate-800 dark:active:bg-slate-900', done && 'opacity-60')}>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[15px] font-medium">{r.template_name}</span>
        <span className="flex min-w-0 gap-1 text-xs text-slate-500">
          <span className="truncate">{[r.location_name, who].filter(Boolean).join(' · ')}</span>
          <span className={clsx('shrink-0', overdue && 'font-semibold text-rose-700 dark:text-rose-400')} data-testid="m-checklist-when">· {when}</span>
        </span>
        {r.kind === 'register' && done && r.register_counts && <span className="block truncate text-xs text-slate-500" data-testid="m-checklist-register-counts">{formatRegisterCounts(r.register_counts)}</span>}
      </span>
      <span className="flex w-12 shrink-0 flex-col items-end gap-1" data-testid="m-checklist-progress" aria-label={`${r.answered_count} of ${r.item_count} answered`}>
        <span className="text-xs font-medium tabular-nums text-slate-700 dark:text-slate-200">{r.answered_count}/{r.item_count}</span>
        <span className="h-1 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700" aria-hidden>
          <span className={clsx('block h-full rounded-full', overdue ? 'bg-rose-600' : done ? 'bg-emerald-600' : 'bg-ink dark:bg-white')} style={{ width: `${pct}%` }} />
        </span>
      </span>
    </Link>
  );
}
